// app/javascript/controllers/scroll_spy_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["section", "tab", "tabContainer"]

    static values = {
        offset: { type: Number, default: 100 },
        activeClass: { type: String, default: "border-b-2 border-slate-800 text-slate-800" },
        inactiveClass: { type: String, default: "text-slate-500 hover:text-slate-800" }
    }

    connect() {
        this.updateActiveTab = this.updateActiveTab.bind(this)
        window.addEventListener('scroll', this.updateActiveTab)
        window.addEventListener('resize', this.updateActiveTab)

        // Initial check
        this.updateActiveTab()
    }

    disconnect() {
        window.removeEventListener('scroll', this.updateActiveTab)
        window.removeEventListener('resize', this.updateActiveTab)
    }

    updateActiveTab() {
        if (!this.hasSectionTarget || !this.hasTabTarget) return

        // Get current scroll position with the offset
        const scrollPosition = window.scrollY + this.offsetValue

        // Find the current section in view
        let currentSection = null

        this.sectionTargets.forEach((section) => {
            const sectionTop = section.offsetTop
            const sectionBottom = sectionTop + section.offsetHeight

            if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                currentSection = section
            }
        })

        if (!currentSection) return

        // Get the section ID
        const currentSectionId = currentSection.id

        // Find the tab that corresponds to this section
        const activeTabIndex = this.tabTargets.findIndex(tab => {
            const href = tab.getAttribute('href')
            return href === `#${currentSectionId}`
        })

        if (activeTabIndex === -1) return

        const previouslyActiveTab = this.tabTargets.find(tab =>
            tab.classList.contains(this.activeClassValue.split(' ')[0])
        )

        // Get the active tab
        const activeTab = this.tabTargets[activeTabIndex]

        // If the active tab has changed, we need to update classes and scroll the tab into view
        if (activeTab !== previouslyActiveTab) {
            // Remove active class from all tabs and add inactive class
            this.tabTargets.forEach(tab => {
                // Remove both active and inactive classes first
                tab.classList.remove(...this.activeClassValue.split(' '))

                // Only add inactive class if this isn't the active tab
                if (tab !== activeTab) {
                    this.inactiveClassValue.split(' ').forEach(cls => {
                        tab.classList.add(cls)
                    })
                }
            })

            // Add active class to the current tab
            this.activeClassValue.split(' ').forEach(cls => {
                activeTab.classList.add(cls)
            })

            // Automatically scroll the active tab into view when scrolling through sections
            this.scrollTabIntoView(activeTab)
        }
    }

    scrollTabIntoView(activeTab) {
        // Find the tab container - either use the target if available, or find the parent
        const tabContainer = this.hasTabContainerTarget
            ? this.tabContainerTarget
            : activeTab.closest('.overflow-x-auto') || activeTab.parentElement;

        if (!tabContainer) return;

        // Get the position of the active tab relative to the container
        const tabRect = activeTab.getBoundingClientRect();
        const containerRect = tabContainer.getBoundingClientRect();

        // Calculate if the tab is partially or fully out of view
        const isTabLeftOfView = tabRect.left < containerRect.left;
        const isTabRightOfView = tabRect.right > containerRect.right;

        if (isTabLeftOfView) {
            // If tab is to the left of the visible area, scroll it into view
            // Add a small offset to give some breathing room
            const scrollLeftPosition = tabContainer.scrollLeft - (containerRect.left - tabRect.left) - 16;
            tabContainer.scrollTo({
                left: scrollLeftPosition,
                behavior: 'smooth'
            });
        } else if (isTabRightOfView) {
            // If tab is to the right of the visible area, scroll it into view
            // Add a small offset to give some breathing room
            const scrollLeftPosition = tabContainer.scrollLeft + (tabRect.right - containerRect.right) + 16;
            tabContainer.scrollTo({
                left: scrollLeftPosition,
                behavior: 'smooth'
            });
        }
    }

    scrollTo(event) {
        event.preventDefault()

        const targetId = event.currentTarget.getAttribute('href').substring(1)
        const targetElement = document.getElementById(targetId)

        if (targetElement) {
            const headerOffset = this.offsetValue
            const elementPosition = targetElement.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            })
        }
    }
}